import axios, { createSecureAxios } from '@/tools/axios/axios'
import RouteParser from '@/tools/route-parser'
import logger from '@/tools/logger/logger'
import google from '@/tools/google'
import getPath from '@/tools/get-path'
import { getUserLocation } from '@/tools/get-user-location'

import normalizeOptionsFromURL from '@/web/components/product-feed-filters/normalize-options-from-url'

import PRODUCT from '@/web/store/types/product'

import FLASH_TYPES from '@/web/store/registered/flash'
import REQUEST_STORE_TYPES from '@/web/store/registered/request'
import STORE_TYPES_PRODUCT_VARIATIONS from '@/web/store/registered/product-variations'

import { getUser, getUserTrackingData } from '@/tools/session'
import { translateObjectKeys, invertObject } from '@/tools/object'
import { PRODUCTS_SEARCH_CONTEXT, getProductContexByRef } from '@/tools/constants/contexts-for-metrics'
import { AUTO_LOAD_SEARCH_ID } from '@/web/components/product-feed/constants'

import SearchProductsService from '@/web/services/search-products'

import Cookies from 'js-cookie'

const COOKIE_NAME = 'custom_zipcode'

let flashSaleInterval
let promise

const isFlashSale = (timerPrice) => {
  return !!(timerPrice && Date.parse(timerPrice.values.expires_at) > new Date())
}

export default {
  [PRODUCT.ACTIONS.LOAD_PRODUCT_DATA]: ({ commit, dispatch }, payload) => {
    if (promise) {
      return promise
    }

    logger.perf('productFetchStart')

    promise = new Promise(resolve => {
      const url = window.location.pathname.replace('/p/', '').split('-')
      const productId = url.pop()
      const productSlug = url.join('-')
      const pagesApiUrl = process.env.PAGES_API_URL
      const { urlSearchPage } = payload
      const customZipcode = Cookies.get(COOKIE_NAME)

      axios.get(`${pagesApiUrl}/products/${productId}/v2.json`)
        .then((response) => {
          const product = response.data
          const { fallback_pricing } = product

          commit(PRODUCT.MUTATIONS.SET_PRODUCT, { ...product, slug: productSlug })

          dispatch(PRODUCT.ACTIONS.LOAD, { fallback_pricing }, { root: true })

          getUser().then(user => {
            if (user.isSignedIn && user.id !== product.seller_id) {
              dispatch(PRODUCT.ACTIONS.LOAD_SHIPPING_PRICE, { productId, customZipcode })
            }
          })

          commit(PRODUCT.MUTATIONS.SET_IS_READY, true)

          resolve(product)
        })
        .catch(() => {
          window.location.href = `${urlSearchPage}?q=${productSlug}`
        })
    })

    return promise
  },
  [PRODUCT.ACTIONS.LOAD]: ({ commit, getters, state, rootState }, payload) => {
    if (!state.id) {
      return false
    }

    logger.perf('productPriceFetchStart')

    commit(PRODUCT.MUTATIONS.SET_IS_FETCHING_PRICE, true)

    if (payload && payload.fallback_pricing) {
      commit(PRODUCT.MUTATIONS.SET_FALLBACK_PRICING, payload.fallback_pricing)
    }

    const shouldIncludeTax = google.isGoogleShoppingProduct(state.id)
    const googleSrc = shouldIncludeTax ? 'google_shopping' : null

    // The timestamp param is required to prevent the browser caching.
    // This endpoint must to be revalidated every time. The cache strategy
    // in this case is only in the backend.
    return new Promise(async (resolve) => {
      const params = getters[REQUEST_STORE_TYPES.GETTERS.GET_PARAMS]

      let endpoint = `/api/v5/products/${state.id}/pricing.json`

      const user = await getUser()

      if (user?.isProductPriceInNewView) {
        endpoint = `/api/pricing/v1/${state.id}`
      }

      const location = await getUserLocation({ timeout: 2000 })

      axios.get(endpoint, {
        params: {
          ...rootState.request.params,
          timestamp: new Date().getTime(),
          city: location?.city,
          state: location?.state,
          search_id: params.rsid,
          page: params.rsp,
          page_index: params.rspix,
          ref: getProductContexByRef(params.ref),
          src: googleSrc,
          color_theme: 'acai',
          action_source: params.action_source
        },
        headers: {
          'X-Browser-ID': getUserTrackingData().uuid
        }
      }).then(({ data }) => {
        const regularPrice = data.regular_price

        commit(PRODUCT.MUTATIONS.SET_OFFER, data.offer)
        commit(PRODUCT.MUTATIONS.SET_PURCHASE, data.purchase)
        commit(PRODUCT.MUTATIONS.SET_REGULAR_PRICE, regularPrice)
        commit(PRODUCT.MUTATIONS.SET_TIMER_PRICE, data.timer_price)
        commit(PRODUCT.MUTATIONS.SET_IS_FLASH_SALE, isFlashSale(data.timer_price))
        commit(PRODUCT.MUTATIONS.SET_CURRENT_USER_ID, data.buyer_id)
        commit(PRODUCT.MUTATIONS.SET_IS_FETCHING_PRICE, false)
        commit(PRODUCT.MUTATIONS.SET_SERVICE_TAX, data.service_tax)
        commit(PRODUCT.MUTATIONS.SET_COUPON, data.coupon_v2)

        if (data.variations) {
          commit(PRODUCT.MUTATIONS.SET_VARIATIONS, data.variations)

          commit(STORE_TYPES_PRODUCT_VARIATIONS.MUTATIONS.SET_ACTIVE_PRODUCT, {
            id: state.id,
            slug: state.slug,
            title: state.title,
            brand: state.brand && state.brand.name,
            price: {
              original: regularPrice.values.price.listed,
              current: regularPrice.values.price.sale
            },
            variations: data.variations,
            photos: state.photos
          })
        }

        if (data.flash_message) {
          commit(FLASH_TYPES.MUTATIONS.SET_FLASH, {
            title: data.flash_message.title,
            subtitle: data.flash_message.subtitle,
            link: data.flash_message.link,
            showCloseButton: false,
            hexColors: {
              title: data.flash_message.colors.title,
              subtitle: data.flash_message.colors.subtitle,
              background: data.flash_message.colors.background
            },
            icon: {
              name: data.flash_message.icon,
              hexColor: data.flash_message.colors.title,
              size: 'medium',
              spaced: 'right'
            }
          })
        }

        if (flashSaleInterval) {
          clearInterval(flashSaleInterval)
        }

        if (isFlashSale(data.timer_price)) {
          flashSaleInterval = setInterval(() => {
            if(!isFlashSale(data.timer_price)) {
              commit(PRODUCT.MUTATIONS.SET_IS_FLASH_SALE, false)
              clearInterval(flashSaleInterval)
            }
          }, 1000)
        }

        resolve()

      }).catch(() => {
        if (payload && payload.fallback_pricing) {
          commit(PRODUCT.MUTATIONS.SET_REGULAR_PRICE, {
            colors: {
              sale_price_text: payload.fallback_pricing.discount ? '#61005D' : '#222',
              discount_text: '#FFFFFF',
              discount_background: '#61005D'
            },
            values: {
              price: payload.fallback_pricing.price,
              discount: payload.fallback_pricing.discount
            }
          })
        }

        commit(PRODUCT.MUTATIONS.SET_IS_FLASH_SALE, false)

        commit(PRODUCT.MUTATIONS.SET_IS_FETCHING_PRICE, false)
      })
    })
  },
  [PRODUCT.ACTIONS.LOAD_MORE_FROM_STORE]: async ({ commit, state }, { sellerId, ownerId }) => {
    const searchProductsService = new SearchProductsService(state.storeProductsPath)
    const location = await getUserLocation({ timeout: 2000 })

    const setMoreFromStoreSearchId = AUTO_LOAD_SEARCH_ID.get_uuid()
    commit(PRODUCT.MUTATIONS.SET_MORE_FROM_STORE_SEARCH_ID, setMoreFromStoreSearchId)

    getUser().then(user => {
      const variables = {
        seller_id: sellerId,
        first: 6,
        search_id: setMoreFromStoreSearchId,
        city: location?.city,
        state: location?.state,
        user_id: user.id,
        new_buyer: user.isNewBuyer,
        search_context: PRODUCTS_SEARCH_CONTEXT.STORE_RELATED
      }

      if (ownerId) {
        variables.owner_id = ownerId
      }

      searchProductsService.fetch({ variables })
        .then(response => {
          commit(PRODUCT.MUTATIONS.SET_MORE_FROM_STORE, {
            isFetching: false,
            products: response.data.seller.search.products.edges
          })
        })
    })
  },
  [PRODUCT.ACTIONS.LOAD_RELATED_PRODUCTS]: async ({ commit, state }, payload) => {
    const product = payload.product
    const searchProductsService = new SearchProductsService(state.relatedProductsPath)

    const params = new RouteParser().toObject(`?${product.relatedProductsQuery}`)
    const normalizedParams = normalizeOptionsFromURL(translateObjectKeys(params, invertObject(payload.abbrParamsMap)))
    const location = await getUserLocation({ timeout: 2000 })

    // Query is passed in the request as `term`.
    normalizedParams.term = normalizedParams.query
    delete normalizedParams.query

    const productRelatedSearchId = AUTO_LOAD_SEARCH_ID.get_uuid()
    commit(PRODUCT.MUTATIONS.SET_RELATED_PRODUCT_SEARCH_ID, productRelatedSearchId)

    getUser().then(user => {
      const variables = Object.assign({
        first: 10,
        search_id: productRelatedSearchId,
        city: location?.city,
        state: location?.state,
        user_id: user.id,
        new_buyer: user.isNewBuyer,
        product_id: product.id,
        search_context: PRODUCTS_SEARCH_CONTEXT.PRODUCTS_RELATED
      }, normalizedParams)

      searchProductsService.fetchSimilar({ variables, endpointModifier: 'similar' })
        .then(response => {
          commit(PRODUCT.MUTATIONS.SET_RELATED_PRODUCTS, {
            isFetching: false,
            products: response.data.similar_search.products.edges
          })
        })
    })
  },
  [PRODUCT.ACTIONS.LOAD_NAVIGATION_HISTORY]: ({ commit, state }, payload) => {
    getUser().then(user => {
      if (user.isSignedIn) {
        return axios.get(`${state.navigationHistoryPath}`)
          .then(({ data }) => {
            commit(PRODUCT.MUTATIONS.SET_NAVIGATION_HISTORY, {
              isFetching: false,
              products: data
            })
          })
          .catch(error => {
            // 404 is expected if the user does not have a navigation history yet.
            if (error.response && error.response.status === 404) { return }

            logger.error(error)
          })
      }
    })
  },
  [PRODUCT.ACTIONS.LOAD_SHIPPING_PRICE]: ({ commit, state }, { productId, customZipcode }) => {
    return axios.get(`/api/v1/products/${productId}/shipping`, {
      params: {
        version: 'v1',
        timestamp: new Date().getTime(),
        custom_zipcode: customZipcode
      }
    })
      .then(response => {
        if (
          response.data.type ||
          response.data.label &&
          !state.isShippingPriceEnable
        ) {
          commit(PRODUCT.MUTATIONS.SET_IS_SHIPPING_PRICE_ENABLE, true)
        }

        commit(PRODUCT.MUTATIONS.SET_SHIPPING, response.data)
      })
  },
  [PRODUCT.ACTIONS.LOAD_SIMILAR_PRODUCT_INFO]: async ({ commit, state }) => {
    const user = await getUser()
    const shouldFetchInfo = user.id && !state.similarProductInfo

    if (!shouldFetchInfo) return

    try {
      const endpoint = getPath('similar-product-info', { id: state.id })
      const { data } = await axios.get(endpoint, {
        params: { timestamp: new Date().getTime() }
      })

      commit(PRODUCT.MUTATIONS.SET_SIMILAR_PRODUCT_INFO, data)
    } catch (err) {
      commit(PRODUCT.MUTATIONS.SET_SIMILAR_PRODUCT_INFO, null)
    }
  },
  [PRODUCT.ACTIONS.LOAD_PRODUCT_ACTIONS]: async ({ commit, state }) => {
    commit(PRODUCT.MUTATIONS.SET_IS_FETCHING_ACTIONS, true)

    const endpoint = getPath('product-page-actions', { product_id: state.id })
    const secureAxios = await createSecureAxios()

    secureAxios.get(endpoint)
      .then(({ data }) => {
        commit(PRODUCT.MUTATIONS.SET_PRODUCT_ACTIONS, data.actions)
        commit(PRODUCT.MUTATIONS.SET_PRODUCT_BANNER, data.banner)
      })
      .finally(() => {
        commit(PRODUCT.MUTATIONS.SET_IS_FETCHING_ACTIONS, false)
      })
  }
}
